/* =============================================================================
 * SG-2026 · Cart-Drawer — Premium-Animation-Override
 * -----------------------------------------------------------------------------
 * Ziel: Schnellere + elegantere Open/Close-Animation als das Theme-Default
 * (400ms clip-path mit dramatischem easing [0.86,0,0.07,1]).
 *
 * Ansatz:
 *   Wir patchen NUR die CartDrawer-Klasse (kein Auto-Replace aller Drawer),
 *   indem wir nach `customElements.whenDefined('cart-drawer')` die
 *   Prototype-Methoden _showTransition und _hideTransition ueberschreiben.
 *
 *   Verwendet die native Web Animations API (element.animate()) — keine
 *   externen Imports noetig, funktioniert in allen modernen Browsern.
 *
 * Timings (Apple/Tesla-Benchmarks):
 *   - Show: 280ms   (war 400ms)
 *   - Hide: 220ms   (war 400ms)
 *   - Overlay-Fade: 240ms
 *   - Easing: ease-out-quart  [0.22, 1, 0.36, 1]
 *
 * BUGFIX-RELEVANT (v2):
 *   Problem im v1: `fill: 'forwards'` hielt den End-Zustand als "animation
 *   effect" im Stack. Beim zweiten Oeffnen kollidierten alte und neue
 *   Animationen -> visueller Glitch. Auch der Kinder-Stagger war fragil,
 *   weil der Cart-Inhalt zwischen Opens dynamisch wechselt.
 *
 *   Loesung:
 *     1) Vor jeder neuen Animation werden ALLE laufenden Animationen auf
 *        den betroffenen Elementen gecancelt (`getAnimations().forEach(cancel)`).
 *     2) Der End-Zustand wird deterministisch als Inline-Style gesetzt
 *        (kein fill:forwards). Inline-Styles werden von laufenden
 *        Animationen ueberschrieben, greifen aber nach Animation-Ende.
 *     3) Kinder-Stagger entfernt — robuster + simpler Slide.
 *
 * Fallback:
 *   Bei prefers-reduced-motion bleibt die Theme-Default-Logik aktiv.
 * ========================================================================== */

(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var SHOW_DURATION    = 280;
  var HIDE_DURATION    = 220;
  var OVERLAY_DURATION = 240;
  var EASING_OUT       = 'cubic-bezier(0.22, 1, 0.36, 1)';   // ease-out-quart
  var EASING_IN        = 'cubic-bezier(0.64, 0, 0.78, 0)';   // ease-in-quart

  function getDirection(drawer) {
    var isMobile = window.matchMedia('(max-width: 699px)').matches;
    if (isMobile) return 'bottom';
    return drawer.getAttribute('open-from') || 'right';
  }

  function getTranslate(direction, closed) {
    if (!closed) return 'translate(0, 0)';
    switch (direction) {
      case 'left':   return 'translateX(-100%)';
      case 'right':  return 'translateX(100%)';
      case 'top':    return 'translateY(-100%)';
      case 'bottom': return 'translateY(100%)';
      default:       return 'translateX(100%)';
    }
  }

  function cancelAll(elements) {
    elements.forEach(function (el) {
      if (!el || typeof el.getAnimations !== 'function') return;
      try {
        el.getAnimations().forEach(function (a) { a.cancel(); });
      } catch (e) { /* noop */ }
    });
  }

  function patchCartDrawer() {
    var CartDrawerClass = window.customElements.get('cart-drawer');
    if (!CartDrawerClass || !CartDrawerClass.prototype) return;
    if (CartDrawerClass.prototype.__sgPatched) return;
    CartDrawerClass.prototype.__sgPatched = true;

    /* ----------------------------------------------------------------------- */
    /*  SHOW                                                                   */
    /* ----------------------------------------------------------------------- */
    CartDrawerClass.prototype._showTransition = function (animate) {
      if (animate === undefined) animate = true;

      this._setInitialPosition();

      var overlay = this.shadowRoot && this.shadowRoot.querySelector('[part="overlay"]');
      var content = this.shadowRoot && this.shadowRoot.querySelector('[part="content"]');
      var self    = this;
      var dir     = getDirection(this);
      var dur     = animate ? SHOW_DURATION : 0;
      var ovDur   = animate ? OVERLAY_DURATION : 0;

      // 1) Alle laufenden Animationen auf relevanten Elementen abbrechen
      cancelAll([this, overlay, content]);

      // 2) Deterministischer End-Zustand via Inline-Styles.
      //    Laufende Animation ueberschreibt diese waehrend der Play-Phase;
      //    nach Ende bleiben diese Styles als "Wahrheit" uebrig.
      this.style.visibility = 'visible';
      this.style.opacity    = '1';
      if (content) {
        content.style.transform = 'translate(0, 0)';
        content.style.opacity   = '1';
      }
      if (overlay) overlay.style.opacity = '1';

      var anims = [];

      if (overlay && ovDur > 0) {
        anims.push(overlay.animate(
          [ { opacity: 0 }, { opacity: 1 } ],
          { duration: ovDur, easing: EASING_OUT }
        ));
      }

      if (content && dur > 0) {
        anims.push(content.animate(
          [
            { transform: getTranslate(dir, true),  opacity: 0.6 },
            { transform: getTranslate(dir, false), opacity: 1   }
          ],
          { duration: dur, easing: EASING_OUT }
        ));
      }

      // Wrapper-Fade synchron mit Content
      if (dur > 0) {
        anims.push(this.animate(
          [ { opacity: 0 }, { opacity: 1 } ],
          { duration: Math.min(dur, 160), easing: EASING_OUT }
        ));
      }

      var allFinished = Promise.all(
        anims.map(function (a) { return a && a.finished ? a.finished : Promise.resolve(); })
      );

      return allFinished.then(function () {
        self.classList.add('show-close-cursor');
      }).catch(function () { /* animation cancelled — silent */ });
    };

    /* ----------------------------------------------------------------------- */
    /*  HIDE                                                                   */
    /* ----------------------------------------------------------------------- */
    CartDrawerClass.prototype._hideTransition = function () {
      var overlay = this.shadowRoot && this.shadowRoot.querySelector('[part="overlay"]');
      var content = this.shadowRoot && this.shadowRoot.querySelector('[part="content"]');
      var self    = this;
      var dir     = getDirection(this);

      // 1) Alte Animationen abbrechen
      cancelAll([this, overlay, content]);

      var anims = [];

      if (content) {
        anims.push(content.animate(
          [
            { transform: getTranslate(dir, false), opacity: 1 },
            { transform: getTranslate(dir, true),  opacity: 0 }
          ],
          { duration: HIDE_DURATION, easing: EASING_IN }
        ));
      }

      if (overlay) {
        anims.push(overlay.animate(
          [ { opacity: 1 }, { opacity: 0 } ],
          { duration: HIDE_DURATION, easing: EASING_IN }
        ));
      }

      anims.push(this.animate(
        [ { opacity: 1 }, { opacity: 0 } ],
        { duration: HIDE_DURATION, easing: EASING_IN }
      ));

      var allFinished = Promise.all(
        anims.map(function (a) { return a && a.finished ? a.finished : Promise.resolve(); })
      );

      return allFinished.then(function () {
        self.classList.remove('show-close-cursor');

        // 2) End-Zustand deterministisch per Inline-Style persistieren.
        //    So "haengt" nichts als animation-effect im Stack.
        self.style.opacity    = '0';
        self.style.visibility = 'hidden';
        if (content) {
          content.style.transform = getTranslate(dir, true);
          content.style.opacity   = '0';
        }
        if (overlay) overlay.style.opacity = '0';
      }).catch(function () { /* animation cancelled — silent */ });
    };
  }

  if (window.customElements.get('cart-drawer')) {
    patchCartDrawer();
  } else if (window.customElements.whenDefined) {
    window.customElements.whenDefined('cart-drawer').then(patchCartDrawer);
  } else {
    document.addEventListener('DOMContentLoaded', patchCartDrawer);
  }
})();
